import {
  Box,
  Button,
  chakra,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Stack,
  Switch,
  Tag,
  TagLabel,
  Text,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FormSelect, FormTextArea } from "../../../../components/Forms";
import { dimensionsKeys, filterActiveDimensionItems, generateArrayOfYears, QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast } from "../../../../utils";
import { useForm, Controller } from "react-hook-form";
import {
  organizationAdministrationKeys,
  organizationQuestionnaireKeys,
  useBulkCreateQuestionnaires,
  useGetOrganizationStructure,
  useGetOrganizationUsers,
  useGetQuestionnaireById,
  useUpdateQuestionnaire,
} from "../../../../hooks/query";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useGetAssigneeTemplates, useGetDimensions } from "../../../../hooks/query/dimensions";
import { MultiSelect } from "../../../../components";
import EsgFormLabel from "../../../../components/Forms/EsgFormLabel";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "../../../../config";
import { useQueryClient } from "react-query";
import LocalSpinner from "../../../../components/LocalSpinner";
import { sortUsersByEmail } from "../../../../utils/userSort";

const ALL_VALUE = "ALL";
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

function generateQuarterRange(startYear, startQuarter, endYear, endQuarter) {
  const result = [];
  let y = parseInt(startYear);
  let qIdx = QUARTERS.indexOf(startQuarter);
  const ey = parseInt(endYear);
  const eqIdx = QUARTERS.indexOf(endQuarter);
  while (y < ey || (y === ey && qIdx <= eqIdx)) {
    result.push({ year: y.toString(), quarter: QUARTERS[qIdx] });
    qIdx++;
    if (qIdx > 3) { qIdx = 0; y++; }
  }
  return result;
}

const ScopeTag = ({ label, isSelected, onClick }) => (
  <WrapItem>
    <Tag
      size="lg"
      borderRadius="full"
      bg={isSelected ? "lightLime" : "gray.100"}
      border="1px solid"
      borderColor={isSelected ? "lime" : "gray.300"}
      color={isSelected ? "gray.800" : "gray.600"}
      fontWeight="600"
      px={4}
      py={2}
      cursor="pointer"
      onClick={onClick}
      _hover={{ borderColor: "lime" }}
    >
      <TagLabel fontSize="14px">{label}</TagLabel>
      <Box as="span" ml={2} fontSize="xs">{isSelected ? "✕" : "+"}</Box>
    </Tag>
  </WrapItem>
);

const SCOPE3_CATEGORIES = [
  { field: "purchasedGoodsAndServicesShouldBeFilled", label: "Cat 1 - Purchased Goods & Services" },
  { field: "capitalGoodsShouldBeFilled", label: "Cat 2 - Capital Goods" },
  { field: "fuelAndEnergyRelatedActivitiesShouldBeFilled", label: "Cat 3 - Fuel & Energy" },
  { field: "upstreamTransportationAndDistributionShouldBeFilled", label: "Cat 4 - Upstream Transport" },
  { field: "wasteGeneratedInOperationsShouldBeFilled", label: "Cat 5 - Waste" },
  { field: "businessTravelShouldBeFilled", label: "Cat 6 - Business Travel" },
  { field: "employeeCommutingShouldBeFilled", label: "Cat 7 - Employee Commuting" },
  { field: "upstreamLeasedAssetsShouldBeFilled", label: "Cat 8 - Upstream Leased Assets", disabled: true },
  { field: "downstreamTransportationAndDistributionShouldBeFilled", label: "Cat 9 - Downstream Transport" },
  { field: "processingOfSoldProductsShouldBeFilled", label: "Cat 10 - Processing of Sold Products", disabled: true },
  { field: "useOfSoldProductsShouldBeFilled", label: "Cat 11 - Use of Sold Products" },
  { field: "treatmentOfSoldProductsShouldBeFilled", label: "Cat 12 - End-of-Life Treatment" },
  { field: "downstreamLeasedAssetsShouldBeFilled", label: "Cat 13 - Downstream Leased Assets", disabled: true },
  { field: "franchisesShouldBeFilled", label: "Cat 14 - Franchises" },
  { field: "investmentsShouldBeFilled", label: "Cat 15 - Investments" },
];


function mapDataToFormData(data) {
  const assigneeId = data?.assigneeId ?? "";
  return {
    id: data?.id,
    description: data?.description ?? "",
    dimensionOne: data?.dimensionItems?.dimensionOneItem ?? ALL_VALUE,
    dimensionTwo: data?.dimensionItems?.dimensionTwoItem ?? ALL_VALUE,
    dimensionThree: data?.dimensionItems?.dimensionThreeItem ?? ALL_VALUE,
    dimensionFour: data?.dimensionItems?.dimensionFourItem ?? ALL_VALUE,
    dimensionFive: data?.dimensionItems?.dimensionFiveItem ?? ALL_VALUE,
    assignee: assigneeId,
    year: data?.period?.year ?? "",
    quarter: data?.period?.quarter ? [{ label: data?.period?.quarter, value: data.period.quarter }] : [],
    activityDataEnabled: data?.configurations?.activityDataEnabled ?? true,
    emissionsDataEnabled: data?.configurations?.emissionsDataEnabled ?? false,
    directEmissionsShouldBeFilled: data?.configurations?.directEmissionsShouldBeFilled ?? false,
    indirectEmissionsShouldBeFilled: data?.configurations?.indirectEmissionsShouldBeFilled ?? false,
    purchasedGoodsAndServicesShouldBeFilled: data?.configurations?.purchasedGoodsAndServicesShouldBeFilled ?? false,
    capitalGoodsShouldBeFilled: data?.configurations?.capitalGoodsShouldBeFilled ?? false,
    fuelAndEnergyRelatedActivitiesShouldBeFilled:
      data?.configurations?.fuelAndEnergyRelatedActivitiesShouldBeFilled ?? false,
    upstreamTransportationAndDistributionShouldBeFilled:
      data?.configurations?.upstreamTransportationAndDistributionShouldBeFilled ?? false,
    wasteGeneratedInOperationsShouldBeFilled: data?.configurations?.wasteGeneratedInOperationsShouldBeFilled ?? false,
    businessTravelShouldBeFilled: data?.configurations?.businessTravelShouldBeFilled ?? false,
    employeeCommutingShouldBeFilled: data?.configurations?.employeeCommutingShouldBeFilled ?? false,
    upstreamLeasedAssetsShouldBeFilled: data?.configurations?.upstreamLeasedAssetsShouldBeFilled ?? false,
    downstreamTransportationAndDistributionShouldBeFilled:
      data?.configurations?.downstreamTransportationAndDistributionShouldBeFilled ?? false,
    processingOfSoldProductsShouldBeFilled: data?.configurations?.processingOfSoldProductsShouldBeFilled ?? false,
    useOfSoldProductsShouldBeFilled: data?.configurations?.useOfSoldProductsShouldBeFilled ?? false,
    treatmentOfSoldProductsShouldBeFilled: data?.configurations?.treatmentOfSoldProductsShouldBeFilled ?? false,
    downstreamLeasedAssetsShouldBeFilled: data?.configurations?.downstreamLeasedAssetsShouldBeFilled ?? false,
    franchisesShouldBeFilled: data?.configurations?.franchisesShouldBeFilled ?? false,
    investmentsShouldBeFilled: data?.configurations?.investmentsShouldBeFilled ?? false,
  };
}

function DataEntryPlanningPage() {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("id");
  // `from=task` means we arrived from a questionnaire detail page — Save and
  // Cancel should bounce back to that task, not the data-entry list.
  const fromTask = searchParams.get("from") === "task";

  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning",
  });

  const [useTemplate, setUseTemplate] = useState(false);
  const [isMultipleDimensions, setIsMultipleDimensions] = useState(false);
  const [expandedDimKey, setExpandedDimKey] = useState("");
  const [selectedDimItems, setSelectedDimItems] = useState([]);
  const [useDefaultAssignee, setUseDefaultAssignee] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [scope3Expanded, setScope3Expanded] = useState(false);
  const [scopeError, setScopeError] = useState("");
  const [quarterError, setQuarterError] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [startYear, setStartYear] = useState((new Date().getFullYear()).toString());
  const [startQuarter, setStartQuarter] = useState("Q1");
  const [endYear, setEndYear] = useState((new Date().getFullYear()).toString());
  const [endQuarter, setEndQuarter] = useState("Q4");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm({});

  const { data: organizationStructure } = useGetOrganizationStructure();

  const { data: existingTask, isLoading: isTaskLoading } = useGetQuestionnaireById(taskId, {
    enabled: !!taskId,
    cacheTime: 0,
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  useGetAssigneeTemplates({
    onSuccess: (data) => {
      setTemplates(data);
    },
    cacheTime: 0,
  });

  const formValues = watch();

  const getDimensionsQueryParams = () => {
    const queryParams = {};
    dimensionsKeys.forEach((key) => {
      if (typeof formValues[key] !== "undefined" && formValues[key] !== ALL_VALUE) {
        queryParams[`${key}Item`] = formValues[key];
      }
    });
    return queryParams;
  };

  const { data: dimensionsData } = useGetDimensions(getDimensionsQueryParams(), {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  useEffect(() => {
    if (taskId && existingTask) {
      reset(mapDataToFormData(existingTask));
      const hasScope3 = SCOPE3_CATEGORIES.some((cat) => existingTask?.configurations?.[cat.field]);
      setScope3Expanded(hasScope3);
    } else if (!taskId) {
      reset(mapDataToFormData());
    }
  }, [reset, taskId, existingTask]);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    if (templateId) {
      const template = templates.find((t) => t.id === Number(templateId) || t.id === templateId);
      if (template) {
        setValue("dimensionOne", template?.dimensionItems?.dimensionOneItem || ALL_VALUE);
        setValue("dimensionTwo", template?.dimensionItems?.dimensionTwoItem || ALL_VALUE);
        setValue("dimensionThree", template?.dimensionItems?.dimensionThreeItem || ALL_VALUE);
        setValue("dimensionFour", template?.dimensionItems?.dimensionFourItem || ALL_VALUE);
        setValue("dimensionFive", template?.dimensionItems?.dimensionFiveItem || ALL_VALUE);
        setValue("assignee", template.assigneeId);
      }
    }
  };

  const { data: users, isLoading: isUsersLoading } = useGetOrganizationUsers({
    onError: (err) => {
      showErrorToast(toast, err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  useEffect(() => {
    if (isUsersLoading) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isUsersLoading]);

  const goBack = () => {
    if (fromTask && taskId) {
      navigate(`${routes.organisationQuestionnaire}?id=${taskId}`);
      return;
    }
    navigate(routes.dataCollectionDataEntryPlanning);
  };

  const { mutate: bulkCreate } = useBulkCreateQuestionnaires({
    onSuccess: (created) => {
      const count = Array.isArray(created) ? created.length : 1;
      showSuccessToast(toast, count > 1 ? t("messages.tasksCreated", { count }) : t("messages.taskCreated"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      queryClient.invalidateQueries(organizationAdministrationKeys.user());
      dispatch(hideAppSpinner());
      goBack();
    },
    onError: (err) => {
      dispatch(hideAppSpinner());
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const { mutate: update } = useUpdateQuestionnaire({
    onSuccess: () => {
      showSuccessToast(toast, t("messages.taskUpdated"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      dispatch(hideAppSpinner());
      goBack();
    },
    onError: (err) => {
      dispatch(hideAppSpinner());
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const availableDimLevels = useMemo(() => {
    if (!organizationStructure?.dimensions) return [];
    return dimensionsKeys
      .filter((k) => organizationStructure.dimensions[k]?.items?.length > 0)
      .map((k) => ({ value: k, label: organizationStructure.dimensions[k].name }));
  }, [organizationStructure]);

  const expandedDimIndex = dimensionsKeys.indexOf(expandedDimKey);

  const ALL_ITEMS_VALUE = "__ALL__";

  const expandedDimItems = useMemo(() => {
    if (!expandedDimKey || !dimensionsData?.[expandedDimKey]?.items) return [];
    const activeItems = filterActiveDimensionItems(dimensionsData[expandedDimKey].items);
    return activeItems.map((item) => ({ label: item.name, value: item.id }));
  }, [expandedDimKey, dimensionsData]);

  const expandedDimOptions = useMemo(() => {
    if (expandedDimItems.length === 0) return [];
    return [{ label: "All", value: ALL_ITEMS_VALUE }, ...expandedDimItems];
  }, [expandedDimItems]);

  const effectiveDimItems = useMemo(() => {
    if (selectedDimItems.some((v) => v.value === ALL_ITEMS_VALUE)) return expandedDimItems;
    return selectedDimItems;
  }, [selectedDimItems, expandedDimItems]);

  const dimItemCount = isMultipleDimensions && effectiveDimItems.length > 0 ? effectiveDimItems.length : 1;

  const recurringRange = useMemo(() => {
    if (!isRecurring) return [];
    return generateQuarterRange(startYear, startQuarter, endYear, endQuarter);
  }, [isRecurring, startYear, startQuarter, endYear, endQuarter]);

  const isEndBeforeStart = isRecurring && recurringRange.length === 0;

  const hasScope1or2 = formValues.directEmissionsShouldBeFilled || formValues.indirectEmissionsShouldBeFilled;
  const hasScope3Sub = SCOPE3_CATEGORIES.some((cat) => formValues[cat.field]);
  const isFormValid = (useDefaultAssignee || !!formValues.assignee)
    && (isRecurring ? recurringRange.length > 0 : (!!formValues.year && formValues.quarter?.length > 0))
    && (hasScope1or2 || hasScope3Sub)
    && (!scope3Expanded || hasScope3Sub);

  const prepareSubmitData = (data) => {
    let hasError = false;

    let periodsToCreate;
    if (isRecurring) {
      periodsToCreate = recurringRange;
      if (periodsToCreate.length === 0) {
        setQuarterError("End period must be after start period");
        hasError = true;
      } else {
        setQuarterError("");
      }
    } else {
      const quarters = data.quarter || [];
      if (quarters.length === 0) {
        setQuarterError("Please select at least one quarter");
        hasError = true;
      } else {
        setQuarterError("");
      }
      periodsToCreate = quarters.map((q) => ({ year: data.year, quarter: q.value }));
    }

    const hasScope1or2 = data.directEmissionsShouldBeFilled || data.indirectEmissionsShouldBeFilled;
    const hasScope3Sub = SCOPE3_CATEGORIES.some((cat) => data[cat.field]);

    if (!hasScope1or2 && !hasScope3Sub) {
      setScopeError("Please select at least one scope");
      hasError = true;
    } else if (scope3Expanded && !hasScope3Sub) {
      setScopeError("Please select at least one Scope 3 category");
      hasError = true;
    } else {
      setScopeError("");
    }

    if (hasError) return;

    const dimItemsToCreate = isMultipleDimensions && effectiveDimItems.length > 0
      ? effectiveDimItems.map((item) => ({ key: expandedDimKey, id: item.value, name: item.label }))
      : [null];

    if (periodsToCreate.length > 0) {
      const tasksToCreate = [];
      periodsToCreate.forEach((period) => {
        dimItemsToCreate.forEach((dimItem) => {
          const buildDimValue = (dimKey) => {
            if (dimItem && dimKey === dimItem.key) return dimItem.id;
            if (dimItem && dimensionsKeys.indexOf(dimKey) > dimensionsKeys.indexOf(dimItem.key)) return null;
            const val = data[dimKey];
            return val !== undefined && val !== ALL_VALUE ? val : null;
          };

          let description;
          if (data.description?.trim() && !isMultipleDimensions) {
            description = data.description;
          } else {
            const parts = [];
            if (period.year) parts.push(period.year);
            if (period.quarter && period.quarter !== "ALL") parts.push(period.quarter);
            let addedDim = false;
            if (dimItem) {
              parts.push(dimItem.name);
              addedDim = true;
            } else {
              const dimKeys = [...dimensionsKeys].reverse();
              for (const dk of dimKeys) {
                const dimVal = buildDimValue(dk);
                if (dimVal) {
                  const dim = organizationStructure?.dimensions?.[dk];
                  const item = dim?.items?.find((i) => i.id === dimVal || i.id?.toString() === dimVal?.toString());
                  if (item) { parts.push(item.name); addedDim = true; break; }
                }
              }
            }
            if (!addedDim && period.year) parts.push("All");
            description = parts.join(" · ");
          }

          const dataToSave = {
            description,
            dimensionItems: {
              dimensionOneItem: buildDimValue("dimensionOne"),
              dimensionTwoItem: buildDimValue("dimensionTwo"),
              dimensionThreeItem: buildDimValue("dimensionThree"),
              dimensionFourItem: buildDimValue("dimensionFour"),
              dimensionFiveItem: buildDimValue("dimensionFive"),
            },
            assigneeId: useDefaultAssignee ? null : data.assignee,
            period: { year: period.year, quarter: period.quarter },
            configurations: {
              activityDataEnabled: data.activityDataEnabled,
              emissionsDataEnabled: data.emissionsDataEnabled,
              directEmissionsShouldBeFilled: data.directEmissionsShouldBeFilled,
              indirectEmissionsShouldBeFilled: data.indirectEmissionsShouldBeFilled,
              purchasedGoodsAndServicesShouldBeFilled: data.purchasedGoodsAndServicesShouldBeFilled,
              capitalGoodsShouldBeFilled: data.capitalGoodsShouldBeFilled,
              fuelAndEnergyRelatedActivitiesShouldBeFilled: data.fuelAndEnergyRelatedActivitiesShouldBeFilled,
              upstreamTransportationAndDistributionShouldBeFilled:
                data.upstreamTransportationAndDistributionShouldBeFilled,
              wasteGeneratedInOperationsShouldBeFilled: data.wasteGeneratedInOperationsShouldBeFilled,
              businessTravelShouldBeFilled: data.businessTravelShouldBeFilled,
              employeeCommutingShouldBeFilled: data.employeeCommutingShouldBeFilled,
              upstreamLeasedAssetsShouldBeFilled: data.upstreamLeasedAssetsShouldBeFilled,
              downstreamTransportationAndDistributionShouldBeFilled:
                data.downstreamTransportationAndDistributionShouldBeFilled,
              processingOfSoldProductsShouldBeFilled: data.processingOfSoldProductsShouldBeFilled,
              useOfSoldProductsShouldBeFilled: data.useOfSoldProductsShouldBeFilled,
              treatmentOfSoldProductsShouldBeFilled: data.treatmentOfSoldProductsShouldBeFilled,
              downstreamLeasedAssetsShouldBeFilled: data.downstreamLeasedAssetsShouldBeFilled,
              franchisesShouldBeFilled: data.franchisesShouldBeFilled,
              investmentsShouldBeFilled: data.investmentsShouldBeFilled,
            },
          };
          tasksToCreate.push(dataToSave);
        });
      });

      if (tasksToCreate.length === 0) return;

      if (taskId) {
        if (tasksToCreate.length !== 1) {
          showErrorToast(toast, "Edit mode supports a single task only");
          return;
        }
        dispatch(showAppSpinner());
        update({ id: taskId, data: tasksToCreate[0] });
      } else {
        dispatch(showAppSpinner());
        bulkCreate({ tasks: tasksToCreate });
      }
    }
  };

  const renderDimensionOptions = (dimKey) => {
    const dimItems = [];
    if (dimensionsData[dimKey] && dimensionsData[dimKey].items && dimensionsData[dimKey].items.length > 0) {
      dimItems.push(...dimensionsData[dimKey].items);
    }
    const currentValue = formValues[dimKey];
    const filtered = filterActiveDimensionItems(dimItems, currentValue);
    return [{ id: ALL_VALUE, name: "All" }, ...filtered].map((item) => (
      <option key={`${dimKey}-${item.id}`} value={item.id}>
        {item.name}
      </option>
    ));
  };

  if (!organizationStructure || !dimensionsData || isUsersLoading || (taskId && isTaskLoading)) {
    return <LocalSpinner />;
  }

  return (
    <Stack spacing={10} p={4} bg="pureWhite" maxW="620px" mx="auto">
      <Box>
        <Heading fontSize="48px" fontWeight="700" color="darkTeal">
          {taskId ? t("modal.editTaskHeader") : t("modal.createTaskHeader")}
        </Heading>
        <Text fontSize="16px" color="gray.500" maxW="75%">
          {taskId ? t("modal.editTaskHeader") : t("modal.createTaskSubTitle")}
        </Text>
      </Box>

      <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
        <VStack
          spacing={2}
          align="stretch"
          sx={{
            "select, textarea": {
              borderRadius: "xl", fontSize: "14px", h: "42px", px: 4,
              borderColor: "gray.300",
              _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
              _hover: { borderColor: "gray.400" },
            },
            "textarea": { borderRadius: "xl", h: "42px", py: 2, resize: "none", overflow: "hidden" },
          }}
        >
          {/* Assignee */}
          <Box py={2}>
            <Flex justify="space-between" align="center" mb={1}>
              <Text fontSize="18px" fontWeight="700" color="gray.800">Assignee</Text>
              {isMultipleDimensions && (
                <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                  <FormLabel htmlFor="default-assignee" mb="0" fontSize="13px" color="gray.500" whiteSpace="nowrap">
                    Use default per dimension
                  </FormLabel>
                  <Switch
                    id="default-assignee"
                    colorScheme="limeShade"
                    size="sm"
                    isChecked={useDefaultAssignee}
                    onChange={(e) => setUseDefaultAssignee(e.target.checked)}
                  />
                </FormControl>
              )}
            </Flex>
            <Text fontSize="13px" color="gray.500" mb={3}>
              {useDefaultAssignee
                ? "Each task will be allocated to the default assignee for its dimension."
                : isMultipleDimensions
                  ? "Select the person responsible for completing these tasks"
                  : "Select the person responsible for completing this task"}
            </Text>
            <FormSelect
              placeholder=" "
              isDisabled={!!taskId || useTemplate || useDefaultAssignee}
              errors={errors}
              maxInlineSize="full"
              {...register("assignee", {
                required: useDefaultAssignee ? false : globalTranslation("entityRequired", { entityName: t("modal.assignee") }),
              })}
            >
              {users
                ? sortUsersByEmail(users).map((assignee) => (
                    <option key={assignee.id} value={assignee.id}>
                      {assignee.email}
                    </option>
                  ))
                : []}
            </FormSelect>
          </Box>

          {/* Period */}
          <Box py={2}>
            <Flex justify="space-between" align="center" mb={1}>
              <Text fontSize="18px" fontWeight="700" color="gray.800">Period</Text>
              {!taskId && (
                <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                  <FormLabel htmlFor="recurring" mb="0" fontSize="13px" color="gray.500" whiteSpace="nowrap">
                    Recurring
                  </FormLabel>
                  <Switch
                    id="recurring"
                    colorScheme="limeShade"
                    size="sm"
                    isChecked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                  />
                </FormControl>
              )}
            </Flex>
            <Text fontSize="13px" color="gray.500" mb={3}>Select the financial year and quarter for this task</Text>

            {isRecurring ? (
              <Flex direction="column" gap={3}>
                <Flex gap={4} align="flex-end">
                  <FormControl flex={1}>
                    <EsgFormLabel>Start Year *</EsgFormLabel>
                    <FormSelect maxInlineSize="full" value={startYear} onChange={(e) => setStartYear(e.target.value)}>
                      {generateArrayOfYears().map((y) => <option key={y} value={y}>{y}</option>)}
                    </FormSelect>
                  </FormControl>
                  <FormControl flex={1}>
                    <EsgFormLabel>Start Quarter *</EsgFormLabel>
                    <FormSelect maxInlineSize="full" value={startQuarter} onChange={(e) => setStartQuarter(e.target.value)}>
                      {QUARTERS.map((q) => <option key={q} value={q}>{q}</option>)}
                    </FormSelect>
                  </FormControl>
                </Flex>
                <Flex gap={4} align="flex-end">
                  <FormControl flex={1}>
                    <EsgFormLabel>End Year *</EsgFormLabel>
                    <FormSelect maxInlineSize="full" value={endYear} onChange={(e) => setEndYear(e.target.value)}>
                      {generateArrayOfYears().map((y) => <option key={y} value={y}>{y}</option>)}
                    </FormSelect>
                  </FormControl>
                  <FormControl flex={1}>
                    <EsgFormLabel>End Quarter *</EsgFormLabel>
                    <FormSelect maxInlineSize="full" value={endQuarter} onChange={(e) => setEndQuarter(e.target.value)}>
                      {QUARTERS.map((q) => <option key={q} value={q}>{q}</option>)}
                    </FormSelect>
                  </FormControl>
                </Flex>
                {isEndBeforeStart && (
                  <Text fontSize="13px" color="red.500">End period must be after start period</Text>
                )}
                {recurringRange.length > 0 && (
                  <Text fontSize="13px" color="limeShade.600" fontWeight="500">
                    This will create {recurringRange.length} task{recurringRange.length !== 1 ? "s" : ""} ({startYear} {startQuarter} → {endYear} {endQuarter})
                  </Text>
                )}
              </Flex>
            ) : (
              <Flex gap={4}>
                <FormSelect
                  label={`${t("modal.financialYear")} *`}
                  type="text"
                  errors={errors}
                  maxInlineSize="full"
                  {...register("year", {
                    required: !isRecurring ? globalTranslation("entityRequired", { entityName: t("modal.financialYear") }) : false,
                  })}
                >
                  {generateArrayOfYears().map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </FormSelect>

                <FormControl minInlineSize="0" maxW="250px">
                  <EsgFormLabel>{t("modal.quarter")} *</EsgFormLabel>
                  <Box mt={1}>
                    <Controller
                      control={control}
                      name="quarter"
                      render={({ field }) => {
                        const allIndividualQuarters = [
                          { label: `${t("modal.quarterLetter")}1`, value: "Q1" },
                          { label: `${t("modal.quarterLetter")}2`, value: "Q2" },
                          { label: `${t("modal.quarterLetter")}3`, value: "Q3" },
                          { label: `${t("modal.quarterLetter")}4`, value: "Q4" },
                        ];
                        const quarterOptions = [
                          { label: t("modal.quarterAll"), value: "ALL" },
                          ...allIndividualQuarters,
                        ];

                        const handleQuarterChange = (newValue) => {
                          if (Array.isArray(newValue)) {
                            const prev = field.value || [];
                            const prevHadAll = prev.some((v) => v.value === "ALL");
                            const newHasAll = newValue.some((v) => v.value === "ALL");

                            if (newHasAll && !prevHadAll) {
                              field.onChange([{ label: t("modal.quarterAll"), value: "ALL" }]);
                            } else if (prevHadAll && newValue.some((v) => v.value !== "ALL")) {
                              field.onChange(newValue.filter((v) => v.value !== "ALL"));
                            } else {
                              field.onChange(newValue);
                            }
                          } else {
                            field.onChange(newValue);
                          }
                        };

                        return (
                          <MultiSelect
                            options={quarterOptions}
                            placeholder=" "
                            value={field.value}
                            onChange={handleQuarterChange}
                            isMulti
                            selectedOptionColorScheme="gray"
                            chakraStyles={{
                              control: (provided, state) => ({
                                ...provided,
                                borderRadius: "xl",
                                minH: "42px", h: "42px",
                                fontSize: "16px",
                                bg: "white",
                                borderColor: state.isFocused ? "lime" : "gray.300",
                                boxShadow: state.isFocused ? "0 0 0 1px var(--chakra-colors-lime)" : "none",
                                _hover: { borderColor: "gray.400" },
                              }),
                            }}
                          />
                        );
                      }}
                    />
                  </Box>
                </FormControl>
              </Flex>
            )}
            {quarterError && !isRecurring && (
              <Text fontSize="13px" color="red.500" mt={1}>{quarterError}</Text>
            )}
          </Box>

          {/* Dimensions */}
          <Box py={2}>
            <Flex justify="space-between" align="center" mb={1}>
              <Text fontSize="18px" fontWeight="700" color="gray.800">Dimensions</Text>
              <Flex gap={4} align="center">
                {!taskId && (
                  <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                    <FormLabel htmlFor="multiple-dims" mb="0" fontSize="13px" color="gray.500" whiteSpace="nowrap">
                      Multiple
                    </FormLabel>
                    <Switch
                      id="multiple-dims"
                      colorScheme="limeShade"
                      size="sm"
                      isChecked={isMultipleDimensions}
                      onChange={(e) => {
                        setIsMultipleDimensions(e.target.checked);
                        if (e.target.checked) { setUseTemplate(false); }
                        else { setExpandedDimKey(""); setSelectedDimItems([]); setUseDefaultAssignee(false); }
                      }}
                    />
                  </FormControl>
                )}
                <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                  <FormLabel htmlFor="use-template" mb="0" fontSize="13px" color="gray.500" whiteSpace="nowrap">
                    {t("modal.useTemplate")}
                  </FormLabel>
                  <Switch
                    id="use-template"
                    colorScheme="limeShade"
                    size="sm"
                    isChecked={useTemplate}
                    isDisabled={isMultipleDimensions}
                    onChange={(e) => {
                      setUseTemplate(e.target.checked);
                      if (e.target.checked) { setIsMultipleDimensions(false); setExpandedDimKey(""); setSelectedDimItems([]); }
                    }}
                  />
                </FormControl>
              </Flex>
            </Flex>
            <Text fontSize="13px" color="gray.500" mb={3}>
              {isMultipleDimensions
                ? "Select a dimension level to create multiple tasks for, then choose which items"
                : "Select the organizational dimensions for this task"}
            </Text>
            <Flex direction="column" gap={3}>
              {useTemplate && (
                <Box>
                  <FormSelect label={t("modal.selectTemplate")} onChange={handleTemplateChange} maxInlineSize="full">
                    <option value="">{t("modal.selectTemplateOption")}</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </FormSelect>
                </Box>
              )}

              {isMultipleDimensions ? (
                <>
                  <Box>
                    <EsgFormLabel>Create for *</EsgFormLabel>
                    <Wrap spacing={2} mt={1}>
                      {availableDimLevels.map((dim) => (
                        <WrapItem key={dim.value}>
                          <Tag
                            size="lg"
                            borderRadius="full"
                            bg={expandedDimKey === dim.value ? "lightLime" : "gray.100"}
                            border="1px solid"
                            borderColor={expandedDimKey === dim.value ? "lime" : "gray.300"}
                            color={expandedDimKey === dim.value ? "gray.800" : "gray.600"}
                            fontWeight="600"
                            px={4}
                            py={2}
                            cursor="pointer"
                            onClick={() => { setExpandedDimKey(expandedDimKey === dim.value ? "" : dim.value); setSelectedDimItems([]); }}
                            _hover={{ borderColor: "lime" }}
                          >
                            <TagLabel fontSize="14px">{dim.label}</TagLabel>
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>

                  {expandedDimKey && dimensionsKeys.slice(0, expandedDimIndex).map((dimKey) => {
                    if (organizationStructure.dimensions[dimKey]) {
                      return (
                        <FormSelect
                          {...register(`${dimKey}`)}
                          key={`${dimKey}-select`}
                          label={`${organizationStructure.dimensions[dimKey].name} *`}
                          errors={errors}
                          maxInlineSize="full"
                        >
                          {renderDimensionOptions(dimKey)}
                        </FormSelect>
                      );
                    }
                    return null;
                  })}

                  {expandedDimKey && expandedDimOptions.length > 0 && (
                    <FormControl>
                      <EsgFormLabel>{organizationStructure.dimensions[expandedDimKey]?.name} *</EsgFormLabel>
                      <Box mt={1}>
                        <MultiSelect
                          options={expandedDimOptions}
                          value={selectedDimItems}
                          onChange={(val) => {
                            const next = val || [];
                            const prevHadAll = selectedDimItems.some((v) => v.value === ALL_ITEMS_VALUE);
                            const nextHasAll = next.some((v) => v.value === ALL_ITEMS_VALUE);
                            if (nextHasAll && !prevHadAll) {
                              setSelectedDimItems([{ label: "All", value: ALL_ITEMS_VALUE }]);
                            } else if (prevHadAll && next.some((v) => v.value !== ALL_ITEMS_VALUE)) {
                              setSelectedDimItems(next.filter((v) => v.value !== ALL_ITEMS_VALUE));
                            } else {
                              setSelectedDimItems(next);
                            }
                          }}
                          isMulti
                          selectedOptionColorScheme="gray"
                          chakraStyles={{
                            control: (provided, state) => ({
                              ...provided,
                              borderRadius: "xl",
                              minH: "42px",
                              fontSize: "14px",
                              bg: "white",
                              borderColor: state.isFocused ? "lime" : "gray.300",
                              boxShadow: state.isFocused ? "0 0 0 1px var(--chakra-colors-lime)" : "none",
                              _hover: { borderColor: "gray.400" },
                            }),
                          }}
                        />
                      </Box>
                      {effectiveDimItems.length > 0 && (
                        <Text fontSize="13px" color="limeShade.600" fontWeight="500" mt={2}>
                          {effectiveDimItems.length} item{effectiveDimItems.length !== 1 ? "s" : ""} selected
                          {isRecurring && recurringRange.length > 0
                            ? ` × ${recurringRange.length} periods = ${effectiveDimItems.length * recurringRange.length} tasks`
                            : dimItemCount > 1 ? ` = ${dimItemCount} tasks` : ""}
                        </Text>
                      )}
                    </FormControl>
                  )}
                </>
              ) : (
                dimensionsKeys.map((dimKey) => {
                  if (organizationStructure.dimensions[dimKey]) {
                    return (
                      <FormSelect
                        {...register(`${dimKey}`)}
                        key={`${dimKey}-select`}
                        label={`${organizationStructure.dimensions[dimKey].name} *`}
                        errors={errors}
                        disabled={(existingTask && existingTask.status !== QUESTIONNAIRE_STATUSES.NEW) || useTemplate}
                        maxInlineSize="full"
                      >
                        {renderDimensionOptions(dimKey)}
                      </FormSelect>
                    );
                  }
                  return null;
                })
              )}
            </Flex>
          </Box>

          {/* Data */}
          <Box py={2}>
            <Text fontSize="18px" fontWeight="700" color="gray.800" mb={4}>Data</Text>
            <EsgFormLabel>Scopes</EsgFormLabel>
            <Text fontSize="13px" color="gray.500" mb={2}>Choose which emission scopes to collect data for</Text>
            <Wrap spacing={2} mb={3}>
              <ScopeTag
                label="Scope 1"
                isSelected={!!formValues.directEmissionsShouldBeFilled}
                onClick={() => setValue("directEmissionsShouldBeFilled", !formValues.directEmissionsShouldBeFilled)}
              />
              <ScopeTag
                label="Scope 2"
                isSelected={!!formValues.indirectEmissionsShouldBeFilled}
                onClick={() => setValue("indirectEmissionsShouldBeFilled", !formValues.indirectEmissionsShouldBeFilled)}
              />
              <ScopeTag
                label="Scope 3"
                isSelected={scope3Expanded}
                onClick={() => {
                  const next = !scope3Expanded;
                  setScope3Expanded(next);
                  if (!next) {
                    SCOPE3_CATEGORIES.forEach((cat) => setValue(cat.field, false));
                  }
                }}
              />
            </Wrap>
            {scopeError && (
              <Text fontSize="13px" color="red.500" mt={1}>{scopeError}</Text>
            )}
            {scope3Expanded && (
              <Stack pl={2} spacing={2} mt={2}>
                {SCOPE3_CATEGORIES.map((cat) => (
                  <Checkbox
                    key={cat.field}
                    colorScheme="limeShade"
                    isChecked={!!formValues[cat.field]}
                    isDisabled={cat.disabled}
                    onChange={(e) => setValue(cat.field, e.target.checked)}
                    size="lg"
                    sx={{ ".chakra-checkbox__control": { borderRadius: "md" } }}
                  >
                    <Text fontSize="14px" color={cat.disabled ? "gray.400" : "gray.700"}>{cat.label}</Text>
                  </Checkbox>
                ))}
              </Stack>
            )}
          </Box>

          {/* Data Type */}
          <Box py={2}>
            <EsgFormLabel>Data Type</EsgFormLabel>
            <Text fontSize="13px" color="gray.500" mb={2}>Choose the type of data to collect</Text>
            <Flex gap={6}>
              <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                <Switch
                  id="activity-data"
                  colorScheme="limeShade"
                  isChecked={!!formValues.activityDataEnabled}
                  onChange={(e) => setValue("activityDataEnabled", e.target.checked)}
                />
                <FormLabel htmlFor="activity-data" mb="0" fontSize="14px" fontWeight="600" color={formValues.activityDataEnabled ? "gray.800" : "gray.400"}>
                  Activity Data
                </FormLabel>
              </FormControl>
              <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                <Switch
                  id="emissions-data"
                  colorScheme="limeShade"
                  isChecked={!!formValues.emissionsDataEnabled}
                  onChange={(e) => setValue("emissionsDataEnabled", e.target.checked)}
                />
                <FormLabel htmlFor="emissions-data" mb="0" fontSize="14px" fontWeight="600" color={formValues.emissionsDataEnabled ? "gray.800" : "gray.400"}>
                  Emissions Data
                </FormLabel>
              </FormControl>
            </Flex>
          </Box>

          {/* Description */}
          <Box py={2}>
            <Text fontSize="18px" fontWeight="700" color="gray.800" mb={1}>Description</Text>
            <Text fontSize="13px" color="gray.500" mb={3}>
              {isMultipleDimensions
                ? "Descriptions will be auto-generated for each task when creating multiple."
                : "Leave blank for auto-generation or add a custom description for the task."}
            </Text>
            <FormTextArea
              type="text"
              errors={errors}
              {...register("description")}
              blockSize="auto"
              rows={1}
              controlProps={{ maxInlineSize: "full" }}
              disabled={isMultipleDimensions}
            />
          </Box>

          <Flex gap={6} pt={6} pb={8}>
            <Button
              onClick={goBack}
              flex={1}
              h="56px"
              borderRadius="xl"
              fontSize="18px"
              bg="transparent"
              color="pureBlack"
              border="1px solid"
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              flex={1}
              h="56px"
              borderRadius="xl"
              fontSize="18px"
              bg="lime"
              color="pureBlack"
              _hover={{ bg: "limeShade.300" }}
              isDisabled={!isFormValid}
            >
              {taskId ? t("modal.save") : t("modal.createTaskHeader")}
            </Button>
          </Flex>
        </VStack>
      </chakra.form>
    </Stack>
  );
}

export default DataEntryPlanningPage;
